import React, { useState, useEffect, useRef } from "react"
import StickyBox from "react-sticky-box"

import UserSessionForm from "../UserSessionForm/UserSessionForm"
import PlaceOrderButton from "./components/forms/PlaceOrderButton"
import DeliveryAddressForm from "./components/forms/DeliveryAddressForm"
import DeliveryTimeSlotForm from "./components/forms/DeliveryTimeSlotForm"
import TakeAwayTimeSlotForm from "./components/forms/TakeAwayTimeSlotForm"
import PaymentDetailsForm from "./components/forms/payment/PaymentDetailsForm"
import BillingDetailsForm from "./components/forms/BillingDetailsForm"
import PhoneDetailsForm from "./components/forms/PhoneDetailsForm"
import OrderPreferencesForm from "./components/forms/OrderPreferencesForm"
import CurrentUserStore from "../stores/CurrentUserStore"
import FluxAuthenticationActions from "../actions/FluxAuthenticationActions"
import AlertModal from "../ui-components/AlertModal"
import ServicesCart from "../CartContainer/components/ServicesCart"

import CartStateStore, {
  CHANGE_STORE,
  getCart,
  getPaymentMethod,
  PLACE_ORDER,
  RESET_PAYMENT_INFO,
  UPDATE_DELIVERY_DETAILS,
  CUSTOMIZE_DELIVERY_PRICE,
  UPDATE_PAYMENT_INFO,
  useCartStateStore,
} from "../stores/CartStateStore"
import getTarget from "../utils/getTarget"
import BrowserLocationHelper from "../utils/browser_location_helper"
import { getFromLocalStorage } from "../utils/localStorage"
import { getTakeAwaySlots } from "../apis/TakeAwaySlots"

import { CreateOrder } from "../apis/OrdersAPI"

import style from "./style.module"
import { TAKE_AWAY_NOT_PERMITTED } from "../constants"
import AlertModalWithBody from "../ui-components/AlertModalWithBody"
import {
  logCashConfirmedOverSoftThreshold,
  logCashDismissedOverSoftThreshold,
} from "../apis/Analytics/gtmActions"
import CustomizeDeliveryPriceForm from "./components/forms/CustomizeDeliveryPriceForm";
import { validateOrder } from "./services/order_validator";

function _CheckoutApp(props) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const {
    user_session_token,
    current_user,
    venue,
    delivery_address,
    deliverySlots,
    createOrderAPIURL,
    stripe_key,
    available_coupons,
    venue_accepts_stripe,
    venue_accepts_cash,
    venue_accepts_satispay,
    show_stripe_method,
    billing_details,
    cashSettings: cashSettingsProp,
    wallet,
    csrfToken
  } = props

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [cashSettings, setCashSettings] = useState({
    ...cashSettingsProp,
    cash_method_confirmed: false,
  })

  const [state, setState] = useState({
    current_user: current_user,
    disabled: false,
    errors: {},
    errorModalText: null,
    timeSlotIndex: null,
    user_session_token: user_session_token,
    stripe_payment_intent_id: null,
    isTakeAway: false,
    takeAwayTimeSlots: null,
    selectedPickingTime: null,
    selectedDeliveryPrice: 0,
    takeAwayTimeSlot: "",
    verifiedCountryPrefix: "",
    verifiedPhoneNumber: "",
  })
  const disableTimer = useRef(null)
  const [cartState, dispatchCartState] = useCartStateStore()

  const [deliveryAddressState, setDeliveryAddressState] = useState({
    street_name: delivery_address.street_name || "",
    street_number: delivery_address.street_number || "",
    city: delivery_address.city || "",
    postal_code: delivery_address.postal_code || "",
    latitude: delivery_address.latitude || "",
    longitude: delivery_address.longitude,
    phone: delivery_address.phone || "",
    phone_confirmation: delivery_address.phone_confirmation || "",
    country_prefix: delivery_address.country_prefix || "+39",
    country_code: delivery_address.country_code || "it",
    delivery_notes: delivery_address.delivery_notes || "",
    restricted_traffic_area: delivery_address.restricted_traffic_area === true,
  }) // TODO: refactor default state

  const [phoneDetailsState, setPhoneDetailsState] = useState({
    isNumberLoaded: false,
    needVerification: false,
    needToModify: false,
    number: "",
    newNumber: "",
    code: "",
    countryCode: "IT",
    countryPrefix: "+39",
    isVerified: false,
    recaptchaVerifying: false,
    error: {
      ok: true,
      type: "",
      message: "",
    },
    isSendingSms: false,
    isConfirmingCode: false,
  }) // TODO: refactor default state

  const [billingDetailsState, setBillingDetailsState] = useState({
    business_name: billing_details.business_name,
    business_address: billing_details.business_address,
    business_invoice_code: billing_details.business_invoice_code,
    vat_code: billing_details.vat_code,
    tax_code: billing_details.tax_code,
    request_invoice: billing_details.request_invoice,
  }) // TODO: refactor default state

  const [OrderPreferencesState, setOrderPreferencesState] = useState({
    baby_sleeping: false,
    order_modifications: "",
  })

  const [alertForNextDaysOrder, setAlertForNextDaysOrder] = useState(null)

  const [walletModalState, setWalletModalState] = useState({
    visible: false,
    cashConfirmed: wallet?.available_amount_cents > 0 ? false : null,
  })

  // -------------------------------------
  // Effects
  // -------------------------------------

  useEffect(() => {
    CurrentUserStore.addChangeListener(handleCurrentUserStoreChange)

    initializeTakeAwaySlots()

    dispatchCartState({ type: CHANGE_STORE, payload: venue })

    return () => {
      CurrentUserStore.removeChangeListener(handleCurrentUserStoreChange)

      clearInterval(disableTimer.current)
    }
  }, [])

  useEffect(() => {
    // Checks when the payment method changes.
    // Show the modal when "cash" is selected with available wallet amount
    // Resets the "cashConfirmed" when cash is de-selected

    // Avoids to display modal when there is no wallet amount
    if (walletModalState.cashConfirmed === null) return

    // Avoids to display modal while loading
    if (
      cartState.paymentMethod.type === "cash" &&
      (cartState.paymentMethod.options?.cash_amount === null ||
        cartState.paymentMethod.options?.cash_amount === undefined)
    ) {
      return
    }

    if (cartState.paymentMethod.type === "cash") {
      if (!walletModalState.cashConfirmed) {
        setWalletModalState({ ...walletModalState, visible: true })
      }
    } else {
      if (walletModalState.cashConfirmed) {
        setWalletModalState({ ...walletModalState, cashConfirmed: false })
      }
    }
  }, [cartState.paymentMethod.type])

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleCurrentUserStoreChange() {
    const user = CurrentUserStore.getUser()

    // @todo: remove this hack. If a user is not logged in we should redirect to the sign_in page instead of the checkout app
    // and when the user signs in so we should redirect to the CheckoutApp
    // this could simplify the logic in Checkout app
    // this workaround is here to let the navbar refresh and display the state correctly once the user is logged in
    BrowserLocationHelper.ReloadPage()

    setState({
      ...state,
      current_user: user,
      user_session_token:
        user && user.user_session ? user.user_session.token : null,
    })
  }

  async function initializeTakeAwaySlots() {
    let isTakeAwaySet = JSON.parse(getFromLocalStorage("take_away"))

    // GUARD: takeaway
    if (isTakeAwaySet && !venue.accepts_take_away) {
      setState({
        ...state,
        errors: { ...state.errors, takeAway: TAKE_AWAY_NOT_PERMITTED },
      })
      return
    }
    // If is a takeaway order get takeaway slots and add them to the state
    if (!!isTakeAwaySet) {
      const { latitude, longitude } = delivery_address
      const venueId = venue && venue.id
      const time = new Date()
      const takeAwaySlots = await getTakeAwaySlots(
        venueId,
        latitude,
        longitude,
        time
      )
      setState({ ...state, isTakeAway: true, takeAwayTimeSlots: takeAwaySlots })
    }
  }

  function handleWalletClick() {
    setWalletModalState({ ...walletModalState, visible: true })
  }

  function handleTimeSlotIndexChanged(index) {
    const slot = deliverySlots && deliverySlots[index]
    const price_cents = slot?.price_cents || 0

    if (slot?.date) {
      let jsDate = new Date(slot.date)
      if (jsDate.getDate() !== new Date().getDate()) {
        setAlertForNextDaysOrder({
          step: "alert-set",
          date: jsDate.toLocaleString(I18n.locale, {
            weekday: "short",
            day: "2-digit",
            month: "long",
          }),
          time: `${slot.start_time}-${slot.end_time}`,
        })
      } else {
        setAlertForNextDaysOrder(null)
      }
    }

    dispatchCartState({
      type: UPDATE_DELIVERY_DETAILS,
      payload: {
        startTime: slot?.startTime,
        price: price_cents,
        notes: slot?.notes,
        errors: [],
      },
    })
    setState({ ...state, timeSlotIndex: index, selectedDeliveryPrice: price_cents })
  }

  function handleDeliveryPriceChanged(price) {
    dispatchCartState({
      type: CUSTOMIZE_DELIVERY_PRICE,
      payload: {
        price: price,
      },
    })

    setState({ ...state, selectedDeliveryPrice: price })
  }

  function handleTakeAwayTimeSlotChange(e) {
    const target = getTarget(e)
    setState({
      ...state,
      selectedPickingTime: target.value,
      takeAwayTimeSlot: target.options[target.selectedIndex].dataset.timeSlot,
    })
  }

  function getSelectedStartTime() {
    const deliverySlot = getSelectedTimeSlot()

    return deliverySlot ? deliverySlot.start_time : null
  }

  function getSelectedTimeSlot() {
    const index = state.timeSlotIndex
    const slots = deliverySlots

    return index && slots[index]
  }

  function getUserSessionToken() {
    return state.user_session_token
  }

  function getOrderHash() {
    const isTakeAway = state.isTakeAway
    const delivery_address = deliveryAddressState
    const billing_details = billingDetailsState
    const phoneDetailsForm = phoneDetailsState
    const deliverySlot = getSelectedTimeSlot()
    const payment_details = cartState.paymentMethod
    const order_preferences = OrderPreferencesState
    const coupon_code = cartState.couponDetails?.code || null
    const timeSlot = deliverySlot
      ? `${deliverySlot.start_time}-${deliverySlot.end_time}`
      : isTakeAway
      ? state.takeAwayTimeSlot
      : null
    const proposed_picking_time = isTakeAway ? state.selectedPickingTime : null
    const vehicleType = deliverySlot ? deliverySlot.vehicle_type : null
    const orderType =
      deliverySlot && deliverySlot.vehicle_type === "venue"
        ? "venue"
        : isTakeAway
        ? "take_away"
        : null

    let order = {
      phone_attributes: {
        country_code: phoneDetailsForm.countryCode,
        country_prefix: phoneDetailsForm.countryPrefix,
        number: phoneDetailsForm.number,
        verified: phoneDetailsForm.isVerified,
      },
      latitude: delivery_address.latitude,
      longitude: delivery_address.longitude,
      street_name: delivery_address.street_name,
      street_number: delivery_address.street_number,
      postal_code: delivery_address.postal_code,
      city: delivery_address.city,
      delivery_notes: delivery_address.delivery_notes,
      restricted_traffic_area: delivery_address.restricted_traffic_area,
      baby_sleeping: order_preferences && order_preferences.baby_sleeping,
      order_modifications:
        order_preferences && order_preferences.order_modifications,
      line_items: getCart(cartState).products,
      venue_id: venue.id,
      time_slot: timeSlot,
      delivery_price_cents: state.selectedDeliveryPrice,
      date: deliverySlot?.date,
      proposed_picking_time: proposed_picking_time,
      vehicle_type: vehicleType,
      coupon_code: coupon_code,
      payment_method: getPaymentMethod(cartState, wallet),
      request_invoice: billing_details.request_invoice,
      locale: I18n.locale,
      metadata: { source: "website" },
    }

    if (billing_details.request_invoice) {
      order.business_name = billing_details.business_name
      order.business_address = billing_details.business_address
      order.business_invoice_code = billing_details.business_invoice_code
      order.vat_code = billing_details.vat_code
      order.tax_code = billing_details.tax_code
    }

    switch (order.payment_method) {
      case "cash":
        order.cash_amount =
          parseFloat(payment_details.options.cash_amount).toFixed(2) || 0
        break
      case "stripe":
        order.payment_attributes = {
          payment_method: payment_details.options.method,
          flow_mode: "async",
          use_wallet: true
        }
        break
      case "satispay":
      case "wallet":
        order.payment_attributes = {
          flow_mode: "async",
          use_wallet: true
        }
        break
    }

    return { order: order, order_type: orderType }
  }


  async function placeOrder(e) {
    e.preventDefault && e.preventDefault()

    let cart = getCart(cartState)

    try {
      const paymentMethod = getPaymentMethod(cartState, wallet)
      const deliverySlot = getSelectedTimeSlot()

      validateOrder(cart, deliverySlot, state.errors, state.isTakeAway, cartState, deliveryAddressState, cashSettings, paymentMethod, phoneDetailsState?.isVerified)
    } catch (error){
      setState({ ...state, errorModalText: error.message, })
      return false
    }

    // Check the cash amount with the soft threshold (soft validation)
    if (
      cartState.paymentMethod?.type === "cash" &&
      cart.total >=
      (cashSettings.soft_threshold_cash_amount_cents ||
        Number.MAX_SAFE_INTEGER) &&
      cashSettings.cash_method_confirmed === false
    ) {
      setCashSettings({ ...cashSettings, cash_method_confirmed: "modal" })
      return
    }

    // Inform the user for the order in future dates
    if (alertForNextDaysOrder?.step === "alert-set") {
      setAlertForNextDaysOrder({
        ...alertForNextDaysOrder,
        step: "alert-active",
      })
      return
    }

    // The order is valid here, so we can proceed
    try {
      dispatchCartState({
        type: UPDATE_PAYMENT_INFO,
        payload: { createdAt: new Date().toISOString() },
      })
      setState({ ...state, disabled: true })

      const order = await CreateOrder(getOrderHash(), getUserSessionToken())

      // order is created, async payment is now processing
      handleOrderCreated(order)
    } catch (error) {
      // remove the payment in progress. The user is aware that something went wrong
      handleOrderCreationError(error)
    }
  }

  function handleOrderCreated(order) {
    if (order?.payment?.method_name === "cash") {
      // 'cash' has no more steps, the order is fine -> cleanup
      dispatchCartState({
        type: PLACE_ORDER,
        payload: { venue_id: order.venue.id, order: order },
      })
    }

    // Wait 2 seconds to be sure the call to external analytics services
    setTimeout(function () {
      BrowserLocationHelper.ChangeURL("/" + I18n.locale + "/orders/" + order.id)
    }, 2000)
  }

  function handleOrderCreationError(error) {
    dispatchCartState({
      type: RESET_PAYMENT_INFO,
      payload: {},
    })

    setState({ ...state, disabled: false })

    if (error.status === 401) {
      FluxAuthenticationActions.unauthorized()
      return
    }

    const body = error?.response?.body
    if (body) {
      const { full_error_messages, detailed_errors } = body
      let message = I18n.t("cart_summary.missing_fields_alert")

      if (full_error_messages && full_error_messages.length > 0) {
        message = full_error_messages.join(", ")
      }

      setState({
        ...state,
        errorModalText: message,
        errors: detailed_errors,
        disabled: false,
      })
    } else {
      setState({
        ...state,
        errorModalText: "Errore",
        errors: "Errore generico",
        disabled: false,
      })
    }
  }

  function isCurrentUserLogged() {
    return state.current_user !== null
  }

  function showDeliverySlotsWidget() {
    return !isTakeAway()
  }

  function isTakeAway() {
    return state.isTakeAway
  }

  function renderCheckoutForm() {
    return (
      <form action="#" className="" onSubmit={placeOrder}>
        <DeliveryAddressForm
          state={deliveryAddressState}
          setState={setDeliveryAddressState}
          venue={venue}
          isTakeAway={state.isTakeAway}
          errors={state.errors}
        />
        <PhoneDetailsForm
          state={phoneDetailsState}
          setState={setPhoneDetailsState}
          userToken={state.user_session_token}
          errors={state.errors}
        />
        {isTakeAway()
          && <TakeAwayTimeSlotForm
            errors={state.errors}
            onChange={handleTakeAwayTimeSlotChange}
            selectedPickingTime={state.selectedPickingTime}
            takeAwaySlots={state.takeAwayTimeSlots}
          />
        }

        {showDeliverySlotsWidget()
          && <DeliveryTimeSlotForm
            slots={deliverySlots}
            index={state.timeSlotIndex}
            onChange={handleTimeSlotIndexChanged}
            errors={state.errors}
          />
        }

        {showDeliverySlotsWidget()
          && <CustomizeDeliveryPriceForm selectedSlot={getSelectedTimeSlot()}
                                         onChange={handleDeliveryPriceChanged}
                                         selectedDeliveryPrice={state.selectedDeliveryPrice}
                                         errors={state.errors}/>
        }

        {cart.total !== null && cart.total !== undefined && (
          <PaymentDetailsForm
            userAuthToken={getUserSessionToken()}
            acceptsCoupons={venue.accepts_discount_codes}
            availableCoupons={available_coupons}
            showAlertMessage={!state.isTakeAway}
            venueId={venue.id}
            startTime={getSelectedStartTime()}
            currency="EUR"
            isTakeAway={state.isTakeAway}
            errors={state.errors}
            venueAcceptsStripe={venue_accepts_stripe}
            venueAcceptsCash={venue_accepts_cash}
            venueAcceptsSatispay={venue_accepts_satispay}
            showStripeMethod={show_stripe_method}
            amount={cart.total + (cart?.wallet_used_amount_cents || 0)}
            deliveryPrice={cartState.deliveryDetails?.price || 0}
            cartState={cartState}
            dispatchCartState={dispatchCartState}
            stripe_key={stripe_key}
            cashSettings={cashSettings}
            wallet={wallet}
            onWalletClick={handleWalletClick}
          />
        )}

        <BillingDetailsForm
          state={billingDetailsState}
          setState={setBillingDetailsState}
          errors={state.errors}
        />
        {!state.isTakeAway && (
          <OrderPreferencesForm
            state={OrderPreferencesState}
            setState={setOrderPreferencesState}
          />
        )}
        <PlaceOrderButton waiting={state.disabled} onClick={placeOrder}/>
      </form>
    )
  }

  function renderUserSessionForm() {
    return (
      <div className="order-summary order-step">
        <UserSessionForm showSignIn={true} csrfToken={csrfToken}/>
      </div>
    )
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  let cart = getCart(cartState)

  // -------------------------------------

  return (
    <div className="container">
      <div className="row">
        <div className="col-md-8">
          <h2>{I18n.t("pages.orders.new.title")}</h2>
          <div className="order-details">
            <div className={style.container}>
              <AlertModal
                title={I18n.t("pages.orders.new.warning")}
                text={state.errorModalText}
                show={!!state.errorModalText}
                onClose={() => setState({ ...state, errorModalText: null })}
              />

              {isCurrentUserLogged()
                ? renderCheckoutForm()
                : renderUserSessionForm()}
            </div>
          </div>
        </div>

        <StickyBox offsetTop={20} className="col-md-4">
          <h2>{I18n.t("pages.orders.new.title_summary")}</h2>
          <div className="order-summary order-step cart-summary-box">
            <ServicesCart
              editable={false}
              cart={cart}
              couponDetails={cartState.couponDetails}
              minDeliveryPrice={cartState.deliveryDetails?.price || 0}
              maxDeliveryPrice={cartState.deliveryDetails?.price || 0}
              notes={cartState.deliveryDetails?.notes}
              isTakeawayActive={state.isTakeAway}
            />
          </div>
        </StickyBox>

        <AlertModalWithBody
          show={alertForNextDaysOrder?.step === "alert-active"}
          onHide={() =>
            setAlertForNextDaysOrder({
              ...alertForNextDaysOrder,
              step: "alert-set",
            })
          }
          onConfirm={placeOrder}
          title={I18n.t("pages.orders.new.confirm_order_next_days")}
          closeLabel={state.disabled ? I18n.t("wait") : I18n.t("pages.orders.new.confirm_cta")}
          confirmDisabled={state.disabled}
        >
          <p>{I18n.t("pages.orders.new.confirm_order_desc")}</p>
          <p>
            {I18n.t("pages.orders.new.confirm_order_desc_date", {
              date: alertForNextDaysOrder?.date,
              time: alertForNextDaysOrder?.time,
            })}
          </p>
        </AlertModalWithBody>

        {cashSettings.soft_threshold_cash_amount_cents && (
          <AlertModalWithBody
            show={cashSettings.cash_method_confirmed === "modal"}
            onHide={() => {
              logCashDismissedOverSoftThreshold()
              setCashSettings({ ...cashSettings, cash_method_confirmed: false })
            }}
            onConfirm={() => {
              logCashDismissedOverSoftThreshold()
              setCashSettings({ ...cashSettings, cash_method_confirmed: false })
            }}
            title={I18n.t("pages.orders.new.big_cash_order.title")}
            closeLabel={I18n.t("pages.orders.new.big_cash_order.change_method")}
            secondActionLabel={I18n.t(
              "pages.orders.new.big_cash_order.use_cash"
            )}
            onSecondAction={() => {
              logCashConfirmedOverSoftThreshold()
              setCashSettings({ ...cashSettings, cash_method_confirmed: true })
            }}
          >
            {I18n.t("pages.orders.new.big_cash_order.desc")}
          </AlertModalWithBody>
        )}

        <AlertModalWithBody
          show={walletModalState.visible}
          onHide={() =>
            setWalletModalState({
              ...walletModalState,
              visible: false,
              cashConfirmed: cartState.paymentMethod?.type === "cash",
            })
          }
          onConfirm={() => {
            setWalletModalState({
              ...walletModalState,
              visible: false,
              cashConfirmed: cartState.paymentMethod?.type === "cash",
            })
          }}
          title={
            cartState.paymentMethod?.type === "cash"
              ? I18n.t("wallet.checkout.modal.cash_title")
              : I18n.t("wallet.checkout.modal.title")
          }
          closeLabel={I18n.t("wallet.checkout.modal.cta")}
        >
          <p>
            {cartState.paymentMethod?.type === "cash"
              ? I18n.t("wallet.checkout.modal.cash_desc")
              : I18n.t("wallet.checkout.modal.desc")}
          </p>
        </AlertModalWithBody>
      </div>
    </div>
  )
}

export default function CheckoutApp(props) {
  return (
    <CartStateStore immutable={true} wallet={props.wallet}>
      <_CheckoutApp {...props}></_CheckoutApp>
    </CartStateStore>
  )
}
